import React from "react";
import { motion } from "framer-motion";
import {
  MapPin, Map, Crown, ShieldCheck, Images, Car, Trees, Sofa, PawPrint,
  CalendarClock, ExternalLink, Globe, Mail, Phone, Send,
} from "lucide-react";
import { useLang } from "@/lib/i18n";
import {
  coverUrl, galleryImages, photoCount, formatPrice, propertyTypeLabel,
  roomsLabel, isVerifiedCover, formatDate, yandexMapUrl, earliestSlotLabel,
  availabilityDate, formatDateNumeric,
} from "@/lib/format";
import { isMappable } from "@/lib/geo";

export const PropertyCard = ({ listing, index = 0, variant = "tile", onHoverStart, onHoverEnd, onViewOnMap }) => {
  const { lang, t } = useLang();
  const isGold = listing?.gold?.isGold === true;
  const specs = listing?.specs || {};
  const feat = listing?.features || {};
  const metro = listing?.metro;
  const contact = listing?.contact;
  const gallery = galleryImages(listing);
  const cover = coverUrl(listing);
  const second = gallery[1]?.url || gallery[0]?.url;
  const count = photoCount(listing);
  const hoverProps = {
    onMouseEnter: () => onHoverStart?.(listing),
    onMouseLeave: () => onHoverEnd?.(listing),
  };

  const mappable = isMappable(listing);

  const bookViewing = () => {
    const returnUrl = `${window.location.origin}${window.location.pathname}${window.location.search}#listing-${listing.id}`;
    const url = new URL(listing.publicUrl || `https://tagdada.com/property/${listing.id}`);
    url.searchParams.set("ref", "realty");
    url.searchParams.set("return", returnUrl);
    window.open(url.toString(), "_blank", "noopener,noreferrer");
  };

  const viewOnMap = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!mappable) return;
    onViewOnMap?.(listing);
  };

  const parkingText = [
    feat.parkingOutdoor ? `${t("parking_out")} ${feat.parkingOutdoor}` : null,
    feat.parkingUnderground ? `${t("parking_under")} ${feat.parkingUnderground}` : null,
  ].filter(Boolean).join(" · ");

  const titleLine = [
    listing.buildingName, specs.area != null ? `${specs.area} м²` : null,
    listing.propertyType !== "parking" ? `${roomsLabel(specs.rooms, t)} комн.` : null,
    specs.floor != null && specs.floor >= 0 ? `${specs.floor} эт.` : null,
  ].filter(Boolean).join(" · ");

  if (variant === "row") {
    return (
      <motion.div
        id={`listing-${listing.id}`}
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.25, delay: Math.min(index * 0.03, 0.3) }}
        data-testid={`property-card-${listing.id}`}
        className="group bg-white rounded-xl overflow-hidden border border-slate-200/90 shadow-sm hover:shadow-md transition-all duration-200 flex flex-row min-h-[112px]"
        {...hoverProps}
      >
        <div className="w-36 shrink-0 bg-slate-100 cursor-pointer overflow-hidden" onClick={bookViewing} data-testid={`card-cover-${listing.id}`}>
          {cover && <img src={cover} alt={listing.title} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />}
        </div>
        <div className="p-3 flex-1 min-w-0 flex flex-col">
          <div className="text-lg font-extrabold text-[#F26B2A] font-mono tracking-tight">
            {formatPrice(listing.price, lang, t)}
          </div>
          <h3 className="mt-0.5 text-sm font-bold tracking-tight text-slate-900 cursor-pointer hover:text-[#F26B2A] line-clamp-1" onClick={bookViewing}>
            {titleLine}
          </h3>
          <p className="mt-0.5 text-xs text-slate-500 line-clamp-1">
            {listing.location?.address || listing.location?.city}
          </p>
          <div className="mt-auto pt-2 flex items-center gap-2">
            <button data-testid={`book-viewing-${listing.id}`} onClick={bookViewing}
              className="inline-flex items-center justify-center gap-1.5 rounded-lg bg-gradient-to-r from-[#F26B2A] to-[#F59B42] hover:from-[#E05A19] hover:to-[#E48A31] text-white text-xs font-bold px-3 py-1.5 shadow-sm transition-all active:scale-[0.98]">
              {t("book_viewing")} <ExternalLink className="h-3.5 w-3.5" />
            </button>
            {mappable && (
              <button
                type="button"
                data-testid={`view-on-map-${listing.id}`}
                onClick={viewOnMap}
                className="inline-flex items-center justify-center gap-1 rounded-lg border border-slate-200 bg-white text-slate-700 hover:border-[#7C3AED] hover:text-[#7C3AED] text-xs font-bold px-2.5 py-1.5 transition-all"
              >
                <Map className="h-3.5 w-3.5" /> {t("view_on_map")}
              </button>
            )}
          </div>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      id={`listing-${listing.id}`}
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: Math.min(index * 0.05, 0.4) }}
      data-testid={`property-card-${listing.id}`}
      className="group bg-white rounded-2xl overflow-hidden border border-slate-200/90 shadow-sm hover:shadow-xl transition-all duration-300 flex flex-col h-full"
      {...hoverProps}
    >
      {/* Photo collage */}
      <div className="relative">
        <div className="grid grid-cols-3 gap-1 h-52 cursor-pointer" onClick={bookViewing} data-testid={`card-cover-${listing.id}`}>
          <div className="col-span-2 overflow-hidden bg-slate-100">
            {cover && <img src={cover} alt={listing.title} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />}
          </div>
          <div className="grid grid-rows-2 gap-1">
            <div className="overflow-hidden bg-slate-100">
              {second && <img src={second} alt="" loading="lazy" className="w-full h-full object-cover" />}
            </div>
            <div className="relative overflow-hidden bg-slate-800">
              {gallery[2]?.url && <img src={gallery[2].url} alt="" loading="lazy" className="w-full h-full object-cover opacity-60" />}
              <div className="absolute inset-0 flex flex-col items-center justify-center text-white">
                <Images className="h-4 w-4 mb-0.5" />
                <span className="text-xs font-bold">{t("all_photos")} ({count})</span>
              </div>
            </div>
          </div>
        </div>
        {/* overlay tags top-left */}
        <div className="absolute top-3 left-3 flex flex-col gap-1.5 items-start">
          {isVerifiedCover(listing) && (
            <span className="bg-white/90 backdrop-blur-md text-[#7C3AED] border border-purple-200 text-[11px] font-semibold px-2 py-1 rounded-md flex items-center gap-1">
              <ShieldCheck className="h-3 w-3" /> {t("verified")}
            </span>
          )}
          {isGold && (
            <span data-testid="gold-featured-badge" className="bg-[#F5C518] text-black text-[11px] font-extrabold px-2.5 py-1 rounded-md flex items-center gap-1 uppercase tracking-wider shadow">
              <Crown className="h-3 w-3" /> GOLD
            </span>
          )}
        </div>
      </div>

      <div className="p-4 flex flex-col flex-1">
        <div className="flex items-baseline justify-between gap-2">
          <div className="text-xl font-extrabold text-[#F26B2A] font-mono tracking-tight">
            {formatPrice(listing.price, lang, t)}
          </div>
          {availabilityDate(listing) && (
            <span className="text-[11px] text-slate-400 whitespace-nowrap shrink-0">
              {t("available_from")} {formatDateNumeric(availabilityDate(listing))}
            </span>
          )}
        </div>

        {/* single bold title = building line */}
        <h3 className="mt-1.5 text-base font-bold tracking-tight text-slate-900 cursor-pointer hover:text-[#F26B2A] transition-colors line-clamp-1" onClick={bookViewing}>
          {titleLine}
        </h3>
        {listing.subtitle && <p className="text-sm text-slate-500 line-clamp-1">{listing.subtitle}</p>}

        {/* address hyperlink + metro */}
        <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1">
          <a href={yandexMapUrl(listing)} target="_blank" rel="noopener noreferrer" data-testid={`card-address-${listing.id}`}
            className="text-sm text-slate-600 hover:text-[#7C3AED] flex items-center gap-1 underline decoration-slate-300 underline-offset-2">
            <MapPin className="h-3.5 w-3.5 shrink-0" />
            {listing.location?.address || listing.location?.city}
          </a>
          {metro && (
            <span className="text-xs text-slate-500 flex items-center gap-1">
              <span className="inline-flex items-center justify-center h-4 w-4 rounded-full text-[9px] font-bold text-white" style={{ backgroundColor: metro.color }}>{metro.line}</span>
              {metro.name} · {metro.distanceMeters} {t("m_unit")}
            </span>
          )}
        </div>

        {/* extra specs */}
        <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1.5 text-xs font-medium text-slate-600">
          {parkingText && <span className="flex items-center gap-1"><Car className="h-3.5 w-3.5 text-slate-400" />{parkingText}</span>}
          {feat.terraceArea ? <span className="flex items-center gap-1"><Trees className="h-3.5 w-3.5 text-slate-400" />{t("terrace")} {feat.terraceArea} м²</span> : null}
          {feat.furnished != null && <span className="flex items-center gap-1"><Sofa className="h-3.5 w-3.5 text-slate-400" />{feat.furnished ? t("furnished_yes") : t("furnished_no")}</span>}
          {feat.petsAllowed != null && <span className="flex items-center gap-1"><PawPrint className="h-3.5 w-3.5 text-slate-400" />{feat.petsAllowed ? t("pets_yes") : t("pets_no")}</span>}
        </div>

        {/* book viewing (pinned to bottom for cross-card alignment) */}
        <div className="mt-4 flex-1" />
        <button data-testid={`book-viewing-${listing.id}`} onClick={bookViewing}
          className="w-full inline-flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-[#F26B2A] to-[#F59B42] hover:from-[#E05A19] hover:to-[#E48A31] text-white font-bold px-4 py-2.5 shadow-sm transition-all active:scale-[0.98]">
          {t("book_viewing")} <ExternalLink className="h-4 w-4" />
        </button>
        {mappable && (
          <button
            type="button"
            data-testid={`view-on-map-${listing.id}`}
            onClick={viewOnMap}
            className="mt-2 w-full inline-flex items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white text-slate-700 hover:border-[#7C3AED] hover:text-[#7C3AED] font-bold px-4 py-2.5 transition-all"
          >
            <Map className="h-4 w-4" /> {t("view_on_map")}
          </button>
        )}
        {listing.availability?.earliestSlot && (
          <p className="mt-1.5 text-xs text-slate-500 text-center flex items-center justify-center gap-1">
            <CalendarClock className="h-3.5 w-3.5" /> {t("earliest_prefix")}: <span className="font-semibold text-slate-700">{earliestSlotLabel(listing.availability.earliestSlot, t, lang)}</span>
          </p>
        )}

        {/* contact + publication date */}
        <div className="mt-4 pt-3 border-t border-slate-100 flex items-start justify-between gap-2">
          {contact ? (
            <div className="flex items-center gap-2 min-w-0">
              {contact.avatar && <img src={contact.avatar} alt={contact.name} className="h-8 w-8 rounded-full shrink-0" />}
              <div className="min-w-0">
                <p className="text-xs font-bold text-slate-900 truncate">{contact.name}</p>
                {contact.company && <p className="text-[11px] text-slate-500 truncate">{contact.company}</p>}
                <div className="flex items-center gap-2 mt-0.5 text-slate-400">
                  {contact.phone && <a href={`tel:${contact.phone}`} onClick={(e) => e.stopPropagation()} className="hover:text-[#F26B2A]"><Phone className="h-3.5 w-3.5" /></a>}
                  {contact.telegram && <a href={`https://t.me/${contact.telegram.replace("@", "")}`} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} className="hover:text-[#F26B2A]"><Send className="h-3.5 w-3.5" /></a>}
                  {contact.email && <a href={`mailto:${contact.email}`} onClick={(e) => e.stopPropagation()} className="hover:text-[#F26B2A]"><Mail className="h-3.5 w-3.5" /></a>}
                  {contact.www && <a href={`https://${contact.www.replace(/^https?:\/\//, "")}`} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} className="hover:text-[#F26B2A]"><Globe className="h-3.5 w-3.5" /></a>}
                </div>
              </div>
            </div>
          ) : <span className="text-xs text-slate-400">{propertyTypeLabel(listing.propertyType, lang)}</span>}
          <span className="text-[11px] text-slate-400 whitespace-nowrap shrink-0">{formatDate(listing.publishedAt, lang)}</span>
        </div>
      </div>
    </motion.div>
  );
};
